import { useState, useEffect } from "react";
import { subirDatos, bajarDatos, eliminarDato } from "./syncSupabase";
import { authHeader } from './supabase';

const COLUMNAS = [
  { key: 'prospecto',     label: 'Prospecto',     color: '#2A7FFF', bg: '#dbeafe', emoji: '👁' },
  { key: 'cotizada',      label: 'Cotizado',       color: '#d97706', bg: '#fef3c7', emoji: '💰' },
  { key: 'aprobada',      label: 'Aprobado',       color: '#16a34a', bg: '#dcfce7', emoji: '✅' },
  { key: 'en_produccion', label: 'En producción',  color: '#6B6FD6', bg: '#E8E9F6', emoji: '🏗' },
  { key: 'perdida',       label: 'Perdido',        color: '#dc2626', bg: '#fee2e2', emoji: '❌' },
];

function diasSinMovimiento(venta) {
  const historial = venta.historialEstado || [];
  const ultima = historial.length > 0
    ? historial[historial.length - 1].fecha
    : (venta.fecha || venta.fechaCreacion);
  if (!ultima) return 0;
  return Math.floor((Date.now() - new Date(ultima).getTime()) / 86400000);
}

function colorDias(dias) {
  if (dias <= 5)  return { color: '#16a34a', bg: '#dcfce7' };
  if (dias <= 14) return { color: '#d97706', bg: '#fef3c7' };
  return { color: '#dc2626', bg: '#fee2e2' };
}

export default function EscritorioComercial({ oscuro, usuario }) {
  const [ventas, setVentas]             = useState([]);
  const [dragId, setDragId]             = useState(null);
  const [dragOver, setDragOver]         = useState(null);
  const [contactoModal, setContactoModal] = useState(null);
  const [notaContacto, setNotaContacto] = useState('');
  const [ariaAnalisis, setAriaAnalisis] = useState('');
  const [ariaLoading, setAriaLoading]   = useState(false);
  const [notas, setNotas]               = useState({ personal: '', equipo: [] });
  const [notaEquipo, setNotaEquipo]     = useState('');
  const [tab, setTab]                   = useState('kanban');

  useEffect(() => {
    const cargarDatos = async () => {
      await bajarDatos('alqos_ventas');
      await bajarDatos('alqos_notas_comerciales');
      await bajarDatos('alqos_agenda_comercial');
      try { setVentas(JSON.parse(localStorage.getItem('alqos_ventas') || '[]')); } catch { setVentas([]); }
      try { setNotas(JSON.parse(localStorage.getItem('alqos_notas_comerciales') || '{"personal":"","equipo":[]}')); } catch {}
    };
    cargarDatos();
  }, []);

  const guardarVentas = (lista) => { setVentas(lista); localStorage.setItem('alqos_ventas', JSON.stringify(lista)); subirDatos('alqos_ventas'); };
  const guardarNotas  = (n)     => { setNotas(n); localStorage.setItem('alqos_notas_comerciales', JSON.stringify(n)); subirDatos('alqos_notas_comerciales'); };

  const onDragStart = (id)      => setDragId(id);
  const onDragOver  = (e, col)  => { e.preventDefault(); setDragOver(col); };
  const onDrop      = (col)     => {
    if (!dragId) return;
    const entrada = { estado: col, fecha: new Date().toISOString().slice(0,10), usuario: usuario?.nombre || 'Sistema', nota: 'Movido desde Kanban' };
    const lista = ventas.map(v => v.id === dragId ? { ...v, estado: col, historialEstado: [...(v.historialEstado||[]), entrada] } : v);
    guardarVentas(lista);
    setDragId(null); setDragOver(null);
  };

  const registrarContacto = () => {
    if (!contactoModal) return;
    const entrada = { estado: contactoModal.estado, fecha: new Date().toISOString().slice(0,10), usuario: usuario?.nombre || 'Sistema', nota: notaContacto || 'Contacto registrado' };
    const lista = ventas.map(v => v.id === contactoModal.id ? { ...v, historialEstado: [...(v.historialEstado||[]), entrada] } : v);
    guardarVentas(lista);
    setContactoModal(null); setNotaContacto('');
  };

  const analizarConAria = async () => {
    setAriaLoading(true); setAriaAnalisis('');
    try {
      const resumen = COLUMNAS.map(col => ({
        etapa: col.label,
        oportunidades: ventas.filter(v => v.estado === col.key).map(v => ({
          cliente: v.cliente, producto: v.producto, monto: v.monto,
          diasSinMovimiento: diasSinMovimiento(v),
        }))
      }));
      const response = await fetch('/api/aria', {
        method: 'POST',
        headers: { ...(await authHeader()),
          'Content-Type': 'application/json',
          'anthropic-version': '2023-06-01',
          },
        body: JSON.stringify({
          model: 'claude-sonnet-4-5',
          max_tokens: 1000,
          system: 'Sos ARIA, asistente de ALQOS, sistema de gestion para pymes graficas industriales argentinas. Sos directa, practica y priorizas el tiempo del equipo comercial.',
          messages: [{ role: 'user', content: 'Analiza este pipeline comercial de una pyme grafica argentina y genera un resumen ejecutivo con: 1) Estado general (2 lineas), 2) Clientes urgentes a contactar HOY con razon, 3) Top 3 acciones concretas para esta semana, 4) Una observacion sobre la distribucion. Pipeline: ' + JSON.stringify(resumen, null, 2) + '. Responde en español, tono directo sin rodeos. Usa bullets. Maximo 220 palabras.' }],
        }),
      });
      const data = await response.json();
      setAriaAnalisis(data.content?.[0]?.text || 'Sin respuesta de ARIA.');
    } catch { setAriaAnalisis('Error al conectar con ARIA. Verificá la API key.'); }
    setAriaLoading(false);
  };

  const alertasRojas = ventas.filter(v => !['entregada','perdida'].includes(v.estado) && diasSinMovimiento(v) > 14);
  const alertasAmber = ventas.filter(v => !['entregada','perdida'].includes(v.estado) && diasSinMovimiento(v) > 7 && diasSinMovimiento(v) <= 14);
  const inp = { padding: '8px 10px', borderRadius: '8px', border: '1px solid #E5E7EB', fontSize: '13px', fontFamily: 'Inter, sans-serif', outline: 'none', boxSizing: 'border-box' };

  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>
      <div style={{ display:'flex', gap:'6px', marginBottom:'16px', background:'white', padding:'4px', borderRadius:'10px', border:'1px solid #E5E7EB', width:'fit-content' }}>
        {[['kanban','🗂 Kanban'],['notas','📝 Notas'],['agenda','📅 Agenda']].map(([id,lb]) => (
          <button key={id} onClick={() => setTab(id)}
            style={{ padding:'6px 16px', borderRadius:'8px', border:'none', fontSize:'12px', fontWeight:'600', cursor:'pointer', background:tab===id?'#1C3FAA':'transparent', color:tab===id?'white':'#6B7280' }}>{lb}</button>
        ))}
      </div>

      {tab === 'kanban' && (<>
        <div style={{ display:'flex', gap:'10px', marginBottom:'14px', flexWrap:'wrap', alignItems:'flex-start' }}>
          {alertasRojas.length > 0 && (
            <div style={{ background:'#fee2e2', border:'1px solid #fecaca', borderRadius:'10px', padding:'9px 14px', fontSize:'12px', color:'#dc2626', fontWeight:'600', flex:1 }}>
              🚨 {alertasRojas.length} sin contacto +14 días: {alertasRojas.map(v => v.cliente).join(' · ')}
            </div>
          )}
          {!alertasRojas.length && alertasAmber.length > 0 && (
            <div style={{ background:'#fef3c7', border:'1px solid #fde68a', borderRadius:'10px', padding:'9px 14px', fontSize:'12px', color:'#d97706', fontWeight:'600', flex:1 }}>
              ⚠️ {alertasAmber.length} sin contacto +7 días: {alertasAmber.map(v => v.cliente).join(' · ')}
            </div>
          )}
          <button onClick={analizarConAria} disabled={ariaLoading}
            style={{ padding:'10px 16px', borderRadius:'10px', border:'none', background:ariaLoading?'#E8E9F6':'#09184D', color:ariaLoading?'#6B7280':'white', fontSize:'12px', fontWeight:'600', cursor:ariaLoading?'wait':'pointer', whiteSpace:'nowrap' }}>
            {ariaLoading ? '🤖 Analizando...' : '🤖 ARIA — Analizar pipeline'}
          </button>
        </div>

        {ariaAnalisis && (
          <div style={{ background:'linear-gradient(135deg,#09184D,#1a2f7a)', borderRadius:'12px', padding:'16px 20px', marginBottom:'16px', color:'white' }}>
            <div style={{ fontSize:'10px', color:'rgba(255,255,255,0.55)', marginBottom:'8px', fontWeight:'700', letterSpacing:'0.12em', textTransform:'uppercase' }}>🤖 ARIA — Análisis de pipeline comercial</div>
            <div style={{ fontSize:'13px', lineHeight:'1.7', color:'rgba(255,255,255,0.9)', whiteSpace:'pre-line' }}>{ariaAnalisis}</div>
            <button onClick={() => setAriaAnalisis('')} style={{ marginTop:'10px', background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.2)', borderRadius:'6px', padding:'4px 10px', fontSize:'11px', color:'rgba(255,255,255,0.7)', cursor:'pointer' }}>Cerrar</button>
          </div>
        )}

        <div style={{ display:'grid', gridTemplateColumns:`repeat(${COLUMNAS.length},1fr)`, gap:'10px', overflowX:'auto' }}>
          {COLUMNAS.map(col => {
            const tarjetas = ventas.filter(v => v.estado === col.key);
            const montoCol = tarjetas.reduce((a,v) => a + Number(v.monto||0), 0);
            return (
              <div key={col.key}
                onDragOver={e => onDragOver(e, col.key)}
                onDragLeave={() => setDragOver(null)}
                onDrop={() => onDrop(col.key)}
                style={{ background:dragOver===col.key?col.bg:'#F9FAFB', borderRadius:'12px', padding:'10px', border:`2px solid ${dragOver===col.key?col.color:'#E5E7EB'}`, minHeight:'420px', transition:'all 0.15s' }}>
                <div style={{ marginBottom:'10px', paddingBottom:'8px', borderBottom:`2px solid ${col.color}25` }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span style={{ fontSize:'12px', fontWeight:'700', color:col.color }}>{col.emoji} {col.label}</span>
                    <span style={{ background:col.bg, color:col.color, fontSize:'11px', fontWeight:'700', padding:'2px 7px', borderRadius:'20px' }}>{tarjetas.length}</span>
                  </div>
                  {montoCol > 0 && <div style={{ fontSize:'10px', color:'#9CA3AF', marginTop:'3px' }}>${montoCol.toLocaleString('es-AR')}</div>}
                </div>
                {tarjetas.map(v => {
                  const dias = diasSinMovimiento(v);
                  const dc = colorDias(dias);
                  const ultimaNota = (v.historialEstado||[]).filter(h => h.nota).slice(-1)[0];
                  return (
                    <div key={v.id} draggable onDragStart={() => onDragStart(v.id)} onDragEnd={() => { setDragId(null); setDragOver(null); }}
                      style={{ background:'white', borderRadius:'10px', padding:'10px 12px', marginBottom:'8px', border:'1px solid #E5E7EB', cursor:'grab', boxShadow:dragId===v.id?'0 6px 20px rgba(0,0,0,0.15)':'0 1px 3px rgba(0,0,0,0.05)', opacity:dragId===v.id?0.5:1 }}>
                      <div style={{ fontWeight:'700', fontSize:'13px', color:'#111827', marginBottom:'2px' }}>{v.cliente}</div>
                      {v.producto && <div style={{ fontSize:'11px', color:'#6B7280', marginBottom:'5px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{v.producto}</div>}
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'5px' }}>
                        {v.monto ? <span style={{ fontSize:'12px', fontWeight:'700', color:'#1C3FAA' }}>${Number(v.monto).toLocaleString('es-AR')}</span> : <span/>}
                        <span style={{ background:dc.bg, color:dc.color, fontSize:'10px', fontWeight:'700', padding:'2px 7px', borderRadius:'6px' }}>
                          {dias === 0 ? 'Hoy' : `${dias}d`}
                        </span>
                      </div>
                      {ultimaNota && (
                        <div style={{ fontSize:'10px', color:'#6B7280', background:'#F9FAFB', borderRadius:'6px', padding:'4px 7px', marginBottom:'6px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          💬 {ultimaNota.nota}
                        </div>
                      )}
                      {v.vendedor && <div style={{ fontSize:'10px', color:'#9CA3AF', marginBottom:'6px' }}>👤 {v.vendedor}</div>}
                      <button onClick={() => { setContactoModal(v); setNotaContacto(''); }}
                        style={{ width:'100%', padding:'6px', borderRadius:'7px', border:'1px solid #E5E7EB', background:'#F9FAFB', fontSize:'11px', fontWeight:'600', cursor:'pointer', color:'#1C3FAA' }}>
                        📞 Registrar contacto
                      </button>
                    </div>
                  );
                })}
                {tarjetas.length === 0 && (
                  <div style={{ textAlign:'center', paddingTop:'40px', color:'#D1D5DB', fontSize:'11px' }}>
                    <div style={{ fontSize:'24px', marginBottom:'6px', opacity:0.4 }}>{col.emoji}</div>Arrastrá acá
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </>)}

      {tab === 'notas' && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
          <div style={{ background:'white', borderRadius:'12px', padding:'16px', border:'1px solid #E5E7EB' }}>
            <div style={{ fontSize:'12px', fontWeight:'700', color:'#1C3FAA', marginBottom:'10px', display:'flex', alignItems:'center', gap:'6px' }}>
              🔒 Mis notas <span style={{ fontSize:'10px', color:'#9CA3AF', fontWeight:'400' }}>solo vos las ves</span>
            </div>
            <textarea value={notas.personal} onChange={e => guardarNotas({ ...notas, personal: e.target.value })}
              placeholder="Tus notas privadas del día... Recordatorios, clientes a llamar, pendientes."
              style={{ width:'100%', minHeight:'320px', border:'1px solid #E5E7EB', borderRadius:'10px', padding:'12px', fontSize:'13px', fontFamily:'Inter, sans-serif', resize:'vertical', outline:'none', color:'#374151', lineHeight:'1.7', boxSizing:'border-box' }} />
            <div style={{ fontSize:'10px', color:'#9CA3AF', marginTop:'5px' }}>✓ Guardado automáticamente</div>
          </div>
          <div style={{ background:'white', borderRadius:'12px', padding:'16px', border:'1px solid #E5E7EB' }}>
            <div style={{ fontSize:'12px', fontWeight:'700', color:'#6B6FD6', marginBottom:'10px', display:'flex', alignItems:'center', gap:'6px' }}>
              👥 Tablero del equipo <span style={{ fontSize:'10px', color:'#9CA3AF', fontWeight:'400' }}>todos pueden leer y escribir</span>
            </div>
            <div style={{ display:'flex', gap:'8px', marginBottom:'10px' }}>
              <input value={notaEquipo} onChange={e => setNotaEquipo(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && notaEquipo.trim()) { const n = { id:Date.now(), texto:notaEquipo, usuario:usuario?.nombre||'Sistema', fecha:new Date().toISOString().slice(0,10), hora:new Date().toTimeString().slice(0,5) }; guardarNotas({ ...notas, equipo:[n,...(notas.equipo||[])] }); setNotaEquipo(''); } }}
                placeholder="Escribí y Enter para publicar al equipo..."
                style={{ ...inp, flex:1, width:'auto' }} />
              <button onClick={() => { if (!notaEquipo.trim()) return; const n = { id:Date.now(), texto:notaEquipo, usuario:usuario?.nombre||'Sistema', fecha:new Date().toISOString().slice(0,10), hora:new Date().toTimeString().slice(0,5) }; guardarNotas({ ...notas, equipo:[n,...(notas.equipo||[])] }); setNotaEquipo(''); }}
                style={{ padding:'8px 14px', borderRadius:'8px', border:'none', background:'#6B6FD6', color:'white', fontSize:'14px', fontWeight:'700', cursor:'pointer' }}>+</button>
            </div>
            <div style={{ maxHeight:'300px', overflowY:'auto' }}>
              {(notas.equipo||[]).length === 0
                ? <div style={{ textAlign:'center', padding:'40px 0', color:'#D1D5DB', fontSize:'12px' }}>Sin notas del equipo todavía</div>
                : (notas.equipo||[]).map((n,i) => (
                  <div key={n.id||i} style={{ background:'#F9FAFB', borderRadius:'8px', padding:'10px 12px', marginBottom:'6px', border:'1px solid #F3F4F6' }}>
                    <div style={{ fontSize:'13px', color:'#374151', lineHeight:'1.5', marginBottom:'4px' }}>{n.texto}</div>
                    <div style={{ fontSize:'10px', color:'#9CA3AF' }}>👤 {n.usuario} · {n.fecha}{n.hora ? ' · '+n.hora : ''}</div>
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      )}

      {tab === 'agenda' && <AgendaComercial usuario={usuario} />}

      {contactoModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'white', borderRadius:'16px', padding:'24px', width:'440px', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize:'15px', fontWeight:'700', color:'#1C3FAA', marginBottom:'4px' }}>📞 Registrar contacto</div>
            <div style={{ fontSize:'12px', color:'#6B7280', marginBottom:'16px' }}>
              <strong style={{ color:'#374151' }}>{contactoModal.cliente}</strong>
              {contactoModal.producto && ' — ' + contactoModal.producto}
            </div>
            <textarea value={notaContacto} onChange={e => setNotaContacto(e.target.value)}
              placeholder="¿Qué se habló? ¿Cuál fue el resultado? Ej: Llamé, está interesado pero pide 5 días para decidir. Volver el jueves."
              style={{ width:'100%', minHeight:'110px', border:'1px solid #E5E7EB', borderRadius:'10px', padding:'10px 12px', fontSize:'13px', fontFamily:'Inter, sans-serif', resize:'vertical', outline:'none', boxSizing:'border-box', lineHeight:'1.6' }} />
            <div style={{ display:'flex', gap:'10px', marginTop:'14px' }}>
              <button onClick={() => setContactoModal(null)} style={{ flex:1, padding:'10px', borderRadius:'10px', border:'1px solid #E5E7EB', background:'white', fontSize:'13px', fontWeight:'600', cursor:'pointer', color:'#6B7280' }}>Cancelar</button>
              <button onClick={registrarContacto} style={{ flex:2, padding:'10px', borderRadius:'10px', border:'none', background:'#1C3FAA', color:'white', fontSize:'13px', fontWeight:'600', cursor:'pointer' }}>✓ Registrar y actualizar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AgendaComercial({ usuario }) {
  const TIPOS = {
    llamada:    { label:'Llamada',     color:'#2A7FFF', emoji:'📞' },
    reunion:    { label:'Reunion',     color:'#6B6FD6', emoji:'🤝' },
    followup:   { label:'Follow-up',   color:'#d97706', emoji:'🔁' },
    entrega:    { label:'Entrega',     color:'#16a34a', emoji:'📦' },
    vencimiento:{ label:'Vencimiento', color:'#dc2626', emoji:'⚠️' },
    otro:       { label:'Otro',        color:'#6B7280', emoji:'📌' },
  };
  const [eventos, setEventos] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ titulo:'', cliente:'', fecha:new Date().toISOString().slice(0,10), hora:'09:00', tipo:'llamada', nota:'', personal:true });

  useEffect(() => {
    try { setEventos(JSON.parse(localStorage.getItem('alqos_agenda_comercial')||'[]')); } catch {}
  }, []);

  const guardar = () => {
    if (!form.titulo.trim()) return;
    const nuevo = { ...form, id:Date.now(), creadoPor:usuario?.nombre||'Sistema', completado:false };
    const lista = [nuevo, ...eventos].sort((a,b) => a.fecha.localeCompare(b.fecha)||a.hora.localeCompare(b.hora));
    setEventos(lista);
    localStorage.setItem('alqos_agenda_comercial', JSON.stringify(lista));
    subirDatos('alqos_agenda_comercial');
    setShowForm(false);
    setForm({ titulo:'', cliente:'', fecha:new Date().toISOString().slice(0,10), hora:'09:00', tipo:'llamada', nota:'', personal:true });
  };

  const toggleCompletado = (id) => {
    const lista = eventos.map(e => e.id===id ? { ...e, completado:!e.completado } : e);
    setEventos(lista); localStorage.setItem('alqos_agenda_comercial', JSON.stringify(lista));
    subirDatos('alqos_agenda_comercial');
  };
  const eliminarEvento = async (id) => {
    const lista = eventos.filter(e => e.id!==id);
    setEventos(lista); localStorage.setItem('alqos_agenda_comercial', JSON.stringify(lista));
    await eliminarDato('alqos_agenda_comercial', id);
  };

  const hoy = new Date().toISOString().slice(0,10);
  const vencidos    = eventos.filter(e => !e.completado && e.fecha < hoy);
  const proximos    = eventos.filter(e => !e.completado && e.fecha >= hoy);
  const completados = eventos.filter(e => e.completado).slice(0,6);
  const inp = { padding:'8px 10px', borderRadius:'8px', border:'1px solid #E5E7EB', fontSize:'13px', fontFamily:'Inter, sans-serif', outline:'none', boxSizing:'border-box', width:'100%' };

  const EventoRow = ({ e, urgente }) => {
    const t = TIPOS[e.tipo]||TIPOS.otro;
    return (
      <div style={{ display:'flex', alignItems:'flex-start', gap:'8px', padding:'9px 0', borderBottom:'1px solid #F3F4F6' }}>
        <span style={{ fontSize:'16px', marginTop:'1px' }}>{t.emoji}</span>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:'12px', fontWeight:'600', color:urgente?'#dc2626':'#374151', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{e.titulo}</div>
          {e.cliente && <div style={{ fontSize:'11px', color:'#6B7280' }}>👤 {e.cliente}</div>}
          <div style={{ fontSize:'10px', color:'#9CA3AF' }}>{e.fecha}{e.hora?' · '+e.hora:''}{!e.personal?' · 👥 equipo':''}</div>
          {e.nota && <div style={{ fontSize:'10px', color:'#6B7280', marginTop:'2px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{e.nota}</div>}
        </div>
        <div style={{ display:'flex', gap:'4px' }}>
          <button onClick={() => toggleCompletado(e.id)} style={{ padding:'4px 8px', borderRadius:'6px', border:'none', background:'#dcfce7', color:'#16a34a', fontSize:'11px', fontWeight:'600', cursor:'pointer' }}>✓</button>
          <button onClick={() => eliminarEvento(e.id)} style={{ padding:'4px 6px', borderRadius:'6px', border:'none', background:'#fee2e2', color:'#dc2626', fontSize:'11px', cursor:'pointer' }}>✕</button>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:'14px' }}>
        <button onClick={() => setShowForm(true)} style={{ padding:'9px 18px', borderRadius:'10px', border:'none', background:'#1C3FAA', color:'white', fontSize:'12px', fontWeight:'600', cursor:'pointer' }}>+ Agregar evento</button>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 280px', gap:'14px' }}>
        <div style={{ background:vencidos.length?'#fff5f5':'white', borderRadius:'12px', padding:'14px', border:`1px solid ${vencidos.length?'#fecaca':'#E5E7EB'}` }}>
          <div style={{ fontSize:'11px', fontWeight:'700', color:vencidos.length?'#dc2626':'#6B7280', marginBottom:'10px', textTransform:'uppercase', letterSpacing:'0.08em' }}>⚠️ Vencidos ({vencidos.length})</div>
          {vencidos.length===0 ? <div style={{ textAlign:'center', padding:'30px 0', color:'#D1D5DB', fontSize:'12px' }}>Sin vencidos 🎉</div> : vencidos.map(e => <EventoRow key={e.id} e={e} urgente />)}
        </div>
        <div style={{ background:'white', borderRadius:'12px', padding:'14px', border:'1px solid #E5E7EB' }}>
          <div style={{ fontSize:'11px', fontWeight:'700', color:'#1C3FAA', marginBottom:'10px', textTransform:'uppercase', letterSpacing:'0.08em' }}>📅 Próximos ({proximos.length})</div>
          {proximos.length===0 ? <div style={{ textAlign:'center', padding:'30px 0', color:'#D1D5DB', fontSize:'12px' }}>Sin eventos próximos</div> : proximos.map(e => <EventoRow key={e.id} e={e} />)}
        </div>
        <div style={{ background:'white', borderRadius:'12px', padding:'14px', border:'1px solid #E5E7EB' }}>
          <div style={{ fontSize:'11px', fontWeight:'700', color:'#16a34a', marginBottom:'10px', textTransform:'uppercase', letterSpacing:'0.08em' }}>✅ Completados</div>
          {completados.length===0 ? <div style={{ textAlign:'center', padding:'30px 0', color:'#D1D5DB', fontSize:'12px' }}>Nada completado aún</div>
            : completados.map(e => (
              <div key={e.id} style={{ display:'flex', alignItems:'center', gap:'6px', padding:'7px 0', borderBottom:'1px solid #F3F4F6', opacity:0.55 }}>
                <span style={{ fontSize:'14px' }}>{TIPOS[e.tipo]?.emoji||'📌'}</span>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:'12px', color:'#6B7280', textDecoration:'line-through', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{e.titulo}</div>
                  {e.cliente && <div style={{ fontSize:'10px', color:'#9CA3AF' }}>{e.cliente}</div>}
                </div>
              </div>
            ))
          }
        </div>
      </div>
      {showForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'white', borderRadius:'16px', padding:'24px', width:'440px', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize:'15px', fontWeight:'700', color:'#1C3FAA', marginBottom:'16px' }}>📅 Nuevo evento</div>
            <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
              <div><label style={{ fontSize:'11px', fontWeight:'600', color:'#6B7280', display:'block', marginBottom:'4px' }}>Título *</label><input style={inp} placeholder="Ej: Llamar a cliente para cotización" value={form.titulo} onChange={e => setForm(f=>({...f,titulo:e.target.value}))} /></div>
              <div><label style={{ fontSize:'11px', fontWeight:'600', color:'#6B7280', display:'block', marginBottom:'4px' }}>Cliente</label><input style={inp} placeholder="Nombre del cliente" value={form.cliente} onChange={e => setForm(f=>({...f,cliente:e.target.value}))} /></div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
                <div><label style={{ fontSize:'11px', fontWeight:'600', color:'#6B7280', display:'block', marginBottom:'4px' }}>Fecha</label><input type="date" style={inp} value={form.fecha} onChange={e => setForm(f=>({...f,fecha:e.target.value}))} /></div>
                <div><label style={{ fontSize:'11px', fontWeight:'600', color:'#6B7280', display:'block', marginBottom:'4px' }}>Hora</label><input type="time" style={inp} value={form.hora} onChange={e => setForm(f=>({...f,hora:e.target.value}))} /></div>
              </div>
              <div>
                <label style={{ fontSize:'11px', fontWeight:'600', color:'#6B7280', display:'block', marginBottom:'6px' }}>Tipo</label>
                <div style={{ display:'flex', gap:'5px', flexWrap:'wrap' }}>
                  {Object.entries(TIPOS).map(([k,{label,color,emoji}]) => (
                    <button key={k} onClick={() => setForm(f=>({...f,tipo:k}))}
                      style={{ padding:'5px 10px', borderRadius:'7px', border:`1.5px solid ${form.tipo===k?color:'#E5E7EB'}`, background:form.tipo===k?'#F0F4FF':'white', color:form.tipo===k?color:'#6B7280', fontSize:'11px', fontWeight:'600', cursor:'pointer' }}>
                      {emoji} {label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label style={{ fontSize:'11px', fontWeight:'600', color:'#6B7280', display:'block', marginBottom:'6px' }}>Visibilidad</label>
                <div style={{ display:'flex', gap:'8px' }}>
                  {[['Solo yo',true,'🔒'],['Todo el equipo',false,'👥']].map(([lb,val,ic]) => (
                    <button key={lb} onClick={() => setForm(f=>({...f,personal:val}))}
                      style={{ flex:1, padding:'7px', borderRadius:'8px', border:`1.5px solid ${form.personal===val?'#1C3FAA':'#E5E7EB'}`, background:form.personal===val?'#F0F4FF':'white', color:form.personal===val?'#1C3FAA':'#6B7280', fontSize:'11px', fontWeight:'600', cursor:'pointer' }}>
                      {ic} {lb}
                    </button>
                  ))}
                </div>
              </div>
              <div><label style={{ fontSize:'11px', fontWeight:'600', color:'#6B7280', display:'block', marginBottom:'4px' }}>Nota</label><textarea style={{ ...inp, minHeight:'60px', resize:'vertical' }} placeholder="Contexto adicional..." value={form.nota} onChange={e => setForm(f=>({...f,nota:e.target.value}))} /></div>
            </div>
            <div style={{ display:'flex', gap:'10px', marginTop:'16px' }}>
              <button onClick={() => setShowForm(false)} style={{ flex:1, padding:'10px', borderRadius:'10px', border:'1px solid #E5E7EB', background:'white', fontSize:'13px', fontWeight:'600', cursor:'pointer', color:'#6B7280' }}>Cancelar</button>
              <button onClick={guardar} style={{ flex:2, padding:'10px', borderRadius:'10px', border:'none', background:'#1C3FAA', color:'white', fontSize:'13px', fontWeight:'600', cursor:'pointer' }}>Guardar evento</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
